import { ClassItem, SubmissionItem, BackupItem, GenreType, VisibilityType } from '../types';
import { api } from '../services/api';
import { showToast } from '../utils/toast';
import { openPreviewModal } from '../components/PreviewModal';
import { captureThumbnail, generateGenreFallback } from '../utils/thumbnail';

export async function renderAdminView(
  container: HTMLElement,
  onNavigate: (path: string) => void
): Promise<void> {
  const token = api.getAdminToken();

  if (!token) {
    renderLoginView(container, () => renderAdminView(container, onNavigate));
    return;
  }

  renderDashboard(container, onNavigate);
}

/**
 * 管理ログイン画面
 */
function renderLoginView(container: HTMLElement, onLoginSuccess: () => void) {
  container.innerHTML = `
    <div style="max-width: 420px; margin: 40px auto; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-md); padding: 32px; box-shadow: var(--shadow-md);">
      <div style="text-align: center; margin-bottom: 24px;">
        <div style="font-size: 40px; margin-bottom: 8px;">🔐</div>
        <h2 style="font-size: 20px; font-weight: 600; color: var(--color-text-primary);">教員専用 管理モード</h2>
        <p style="font-size: 13px; color: var(--color-text-secondary); margin-top: 4px;">
          授業の作成や提出物の管理・リストアを行います。
        </p>
      </div>

      <form id="admin-login-form">
        <div class="form-group" style="margin-bottom: 20px;">
          <label class="form-label" for="admin-pass">
            管理者パスワード
            <span class="badge-req">必須</span>
          </label>
          <input
            type="password"
            id="admin-pass"
            class="form-input"
            placeholder="パスワードを入力 (デモ: admin2026)"
            required
            autocomplete="current-password"
          />
        </div>

        <button type="submit" class="btn btn-primary" style="width: 100%; height: 40px;">
          ログイン
        </button>
      </form>
    </div>
  `;

  const form = container.querySelector('#admin-login-form') as HTMLFormElement;
  const passInput = container.querySelector('#admin-pass') as HTMLInputElement;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const val = passInput.value.trim();
    const ok = await api.adminLogin(val);
    if (ok) {
      showToast('管理者としてログインしました', 'success');
      onLoginSuccess();
    } else {
      showToast('パスワードが違います (デモ用: admin2026)', 'error');
      passInput.select();
    }
  });
}

/**
 * 管理ダッシュボード本体
 */
async function renderDashboard(container: HTMLElement, onNavigate: (path: string) => void) {
  let activeTab: 'classes' | 'submissions' | 'backups' = 'classes';

  const renderTab = async () => {
    const classes = await api.getClasses();
    const submissions = await api.getSubmissions();
    const backups = await api.getBackups();

    container.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px;">
        <div>
          <h1 style="font-size: 24px; font-weight: 600; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
            <span>⚙️</span> 教員管理ダッシュボード
          </h1>
          <p style="font-size: 13px; color: var(--color-text-secondary);">
            授業スペースの運用、提出物の保守、誤上書きの復旧を一元管理します。
          </p>
        </div>

        <div style="display: flex; gap: 10px;">
          <button id="btn-admin-logout" class="btn btn-outlined btn-sm" type="button">
            ログアウト
          </button>
        </div>
      </div>

      <!-- 管理タブバー -->
      <nav class="tab-bar" style="margin-bottom: 20px;" aria-label="管理ナビゲーション">
        <button class="tab-btn ${activeTab === 'classes' ? 'active' : ''}" id="tab-manage-classes" type="button">
          🏫 授業管理 (${classes.length})
        </button>
        <button class="tab-btn ${activeTab === 'submissions' ? 'active' : ''}" id="tab-manage-submissions" type="button">
          📁 提出物管理 (${submissions.length})
        </button>
        <button class="tab-btn ${activeTab === 'backups' ? 'active' : ''}" id="tab-manage-backups" type="button">
          🛡️ リストア＆バックアップ (${backups.length})
        </button>
      </nav>

      <!-- タブコンテンツエリア -->
      <div id="admin-tab-content"></div>
    `;

    // ログアウト
    container.querySelector('#btn-admin-logout')!.addEventListener('click', () => {
      api.adminLogout();
      showToast('ログアウトしました', 'info');
      onNavigate('/');
    });

    // タブ切り替え
    container.querySelector('#tab-manage-classes')!.addEventListener('click', () => {
      activeTab = 'classes';
      renderTab();
    });
    container.querySelector('#tab-manage-submissions')!.addEventListener('click', () => {
      activeTab = 'submissions';
      renderTab();
    });
    container.querySelector('#tab-manage-backups')!.addEventListener('click', () => {
      activeTab = 'backups';
      renderTab();
    });

    const tabContent = container.querySelector('#admin-tab-content') as HTMLElement;

    if (activeTab === 'classes') {
      renderClassesTab(tabContent, classes, renderTab);
    } else if (activeTab === 'submissions') {
      renderSubmissionsTab(tabContent, submissions, classes, renderTab);
    } else if (activeTab === 'backups') {
      renderBackupsTab(tabContent, backups, renderTab);
    }
  };

  renderTab();
}

/**
 * 授業管理タブ
 */
function renderClassesTab(container: HTMLElement, classes: ClassItem[], onRefresh: () => void) {
  container.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
      <h3 style="font-size: 16px; font-weight: 600;">登録中の授業一覧</h3>
      <button id="btn-create-class" class="btn btn-primary btn-sm" type="button">
        ➕ 新規授業の作成
      </button>
    </div>

    <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px;">
      ${classes.map((cls) => `
        <div style="background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; padding: 20px; box-shadow: var(--shadow-card); display: flex; flex-direction: column;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
            <h4 style="font-size: 16px; font-weight: 600; color: var(--color-text-primary);">${escapeHtml(cls.name)}</h4>
            <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background: var(--color-primary);"></span>
          </div>

          <p style="font-size: 12px; color: var(--color-text-secondary); margin-bottom: 16px; flex: 1;">
            ${escapeHtml(cls.description || '説明なし')}
          </p>

          <div style="background: #fafbfc; border: 1px solid var(--color-border-subtle); border-radius: 6px; padding: 10px; font-size: 12px; margin-bottom: 16px; display: flex; flex-direction: column; gap: 4px;">
            <div><strong>ID/URLスラッグ:</strong> <code>${cls.id}</code></div>
            <div><strong>提出パスフレーズ:</strong> <code>${cls.passphrase || 'demo'}</code></div>
          </div>

          <div style="display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap;">
            <button class="btn btn-primary btn-sm btn-bulk-import-class" data-id="${cls.id}" type="button">
              📥 一括インポート
            </button>
            <button class="btn btn-outlined btn-sm btn-copy-class-links" data-id="${cls.id}" type="button">
              📋 リンク取得
            </button>
            <button class="btn btn-text btn-sm btn-delete-class" data-id="${cls.id}" style="color: var(--color-danger);" type="button">
              削除
            </button>
          </div>
        </div>
      `).join('')}
    </div>
  `;

  // 授業指定で一括インポート
  container.querySelectorAll('.btn-bulk-import-class').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id') || undefined;
      openBulkImportModal(classes, id, onRefresh);
    });
  });

  // リンクコピー
  container.querySelectorAll('.btn-copy-class-links').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      const origin = window.location.origin;
      const text = `【提出箱URL】\n${origin}/c/${id}/submit\n【ライブラリURL】\n${origin}/c/${id}/library`;
      navigator.clipboard.writeText(text);
      showToast('授業用リンクをコピーしました！学生に案内できます。', 'success');
    });
  });

  // 授業削除
  container.querySelectorAll('.btn-delete-class').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-id')!;
      if (confirm(`授業「${id}」を削除しますか？`)) {
        await api.deleteClass(id);
        showToast('授業を削除しました', 'success');
        onRefresh();
      }
    });
  });

  // 新規授業作成
  container.querySelector('#btn-create-class')!.addEventListener('click', () => {
    openCreateClassModal(onRefresh);
  });
}

/**
 * 新規授業作成モーダル
 */
function openCreateClassModal(onCreated: () => void) {
  const modalRoot = document.getElementById('modal-root')!;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay active';

  overlay.innerHTML = `
    <div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="class-create-title">
      <div class="modal-header">
        <div class="modal-title" id="class-create-title">
          <span>🏫</span> 新規授業スペースの作成
        </div>
      </div>
      <form id="create-class-form">
        <div class="modal-body">
          <div class="form-group" style="margin-bottom: 14px;">
            <label class="form-label" for="cls-name">
              授業名
              <span class="badge-req">必須</span>
            </label>
            <input type="text" id="cls-name" class="form-input" placeholder="例: 2026年度 Web演習B" required />
          </div>

          <div class="form-group" style="margin-bottom: 14px;">
            <label class="form-label" for="cls-id">
              授業ID (URLスラッグ)
              <span class="badge-req">必須 (半角英数ハイフン)</span>
            </label>
            <input type="text" id="cls-id" class="form-input" placeholder="例: web2026-b" pattern="[a-zA-Z0-9_-]+" required />
          </div>

          <div class="form-group" style="margin-bottom: 14px;">
            <label class="form-label" for="cls-pass">
              提出パスフレーズ
              <span class="badge-req">必須</span>
            </label>
            <input type="text" id="cls-pass" class="form-input" placeholder="学生に共有する簡易合言葉 (例: pass2026)" required />
          </div>

          <div class="form-group" style="margin-bottom: 14px;">
            <label class="form-label" for="cls-desc">
              授業の説明 / 担当教員
              <span class="badge-opt">任意</span>
            </label>
            <textarea id="cls-desc" class="form-textarea" placeholder="概要や受講生へのメッセージ"></textarea>
          </div>

          <div class="form-group">
            <label class="form-label" for="cls-theme">
              テーマカラー
            </label>
            <select id="cls-theme" class="form-select">
              <option value="blue">ブルー (標準)</option>
              <option value="green">グリーン</option>
              <option value="teal">ティール</option>
              <option value="purple">パープル</option>
              <option value="amber">アンバー</option>
              <option value="red">レッド</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" id="btn-cancel-create-cls" class="btn btn-text">キャンセル</button>
          <button type="submit" class="btn btn-primary">作成する</button>
        </div>
      </form>
    </div>
  `;

  modalRoot.appendChild(overlay);

  const cleanup = () => {
    overlay.classList.remove('active');
    setTimeout(() => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 200);
  };

  overlay.querySelector('#btn-cancel-create-cls')!.addEventListener('click', cleanup);

  const form = overlay.querySelector('#create-class-form') as HTMLFormElement;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const id = (overlay.querySelector('#cls-id') as HTMLInputElement).value.trim();
    const name = (overlay.querySelector('#cls-name') as HTMLInputElement).value.trim();
    const passphrase = (overlay.querySelector('#cls-pass') as HTMLInputElement).value.trim();
    const description = (overlay.querySelector('#cls-desc') as HTMLTextAreaElement).value.trim();
    const themeColor = (overlay.querySelector('#cls-theme') as HTMLSelectElement).value as any;

    await api.saveClass({
      id,
      name,
      description,
      passphrase,
      themeColor,
      createdAt: new Date().toISOString()
    });

    showToast(`授業「${name}」を作成しました`, 'success');
    cleanup();
    onCreated();
  });
}

/**
 * 提出物管理タブ
 */
function renderSubmissionsTab(
  container: HTMLElement,
  submissions: SubmissionItem[],
  classes: ClassItem[],
  onRefresh: () => void
) {
  container.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 12px;">
      <div>
        <h3 style="font-size: 16px; font-weight: 600;">全提出物一覧 (${submissions.length}件)</h3>
        <p style="font-size: 12px; color: var(--color-text-secondary);">学生から回収したHTMLファイルをまとめて「限定公開」として授業に流し込むことができます。</p>
      </div>
      <button id="btn-open-bulk-import" class="btn btn-primary btn-sm" type="button">
        📥 HTML一括インポート（限定公開）
      </button>
    </div>

    <div style="background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; overflow-x: auto;">
      <table style="width: 100%; border-collapse: collapse; font-size: 13px; text-align: left;">
        <thead style="background: #fafbfc; border-bottom: 1px solid var(--color-border);">
          <tr>
            <th style="padding: 12px 16px;">作品名 / ファイル名</th>
            <th style="padding: 12px 16px;">授業</th>
            <th style="padding: 12px 16px;">ジャンル</th>
            <th style="padding: 12px 16px;">作者</th>
            <th style="padding: 12px 16px;">公開範囲</th>
            <th style="padding: 12px 16px;">バックアップ</th>
            <th style="padding: 12px 16px; text-align: right;">アクション</th>
          </tr>
        </thead>
        <tbody>
          ${submissions.length === 0 ? `
            <tr><td colspan="7" style="padding: 32px; text-align: center; color: var(--color-text-secondary);">提出物はまだありません</td></tr>
          ` : submissions.map((sub, idx) => `
            <tr style="border-bottom: 1px solid var(--color-border-subtle);">
              <td style="padding: 12px 16px;">
                <div style="font-weight: 600; color: var(--color-text-primary);">${escapeHtml(sub.title)}</div>
                <div style="font-size: 11px; color: var(--color-text-tertiary);"><code>${sub.fileName}</code></div>
              </td>
              <td style="padding: 12px 16px;">
                <span class="badge-opt" title="${sub.classId}">${escapeHtml(classes.find(c => c.id === sub.classId)?.name || sub.classId)}</span>
              </td>
              <td style="padding: 12px 16px;">${sub.genre}</td>
              <td style="padding: 12px 16px;">${escapeHtml(sub.author)}</td>
              <td style="padding: 12px 16px;">
                <span class="card-badge ${sub.visibility === 'public' ? 'badge-public' : 'badge-private'}" style="position: static; padding: 2px 6px;">
                  ${sub.visibility === 'public' ? '完全公開' : '限定公開'}
                </span>
              </td>
              <td style="padding: 12px 16px;">
                ${sub.backupCount > 0 ? `
                  <span style="color: var(--color-primary); font-weight: 500;">${sub.backupCount} 世代</span>
                ` : `
                  <span style="color: var(--color-text-tertiary);">なし</span>
                `}
              </td>
              <td style="padding: 12px 16px; text-align: right; white-space: nowrap;">
                <button class="btn btn-text btn-sm btn-sub-preview" data-idx="${idx}">プレビュー</button>
                <button class="btn btn-outlined btn-sm btn-sub-edit" data-idx="${idx}">HTML編集</button>
                <button class="btn btn-text btn-sm btn-sub-del" data-class="${sub.classId}" data-file="${sub.fileName}" style="color: var(--color-danger);">削除</button>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;

  // 一括インポートモーダル
  container.querySelector('#btn-open-bulk-import')?.addEventListener('click', () => {
    openBulkImportModal(classes, classes[0]?.id, onRefresh);
  });

  // プレビュー
  container.querySelectorAll('.btn-sub-preview').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.getAttribute('data-idx') || '0', 10);
      openPreviewModal(submissions[idx]);
    });
  });

  // HTML直接エディタ
  container.querySelectorAll('.btn-sub-edit').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.getAttribute('data-idx') || '0', 10);
      openHtmlEditorModal(submissions[idx], onRefresh);
    });
  });

  // 削除
  container.querySelectorAll('.btn-sub-del').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const classId = btn.getAttribute('data-class')!;
      const file = btn.getAttribute('data-file')!;
      if (confirm(`提出物「${file}」を削除しますか？`)) {
        await api.deleteSubmission(classId, file);
        showToast('提出物を削除しました', 'success');
        onRefresh();
      }
    });
  });
}

interface QueuedImportFile {
  fileName: string;
  fileContent: string;
  fileSizeBytes: number;
  title: string;
  author: string;
  genre: GenreType;
}

/**
 * ファイル名とHTML本文から作品タイトル・制作者・ジャンルを自動推定
 */
function inferMetadataFromFile(fileName: string, htmlContent: string, defaultGenre: GenreType): {
  title: string;
  author: string;
  genre: GenreType;
} {
  const baseName = fileName.replace(/\.html?$/i, '').trim();
  let author = '匿名';
  let nameDerivedTitle = baseName;

  // パターン1: 学籍番号_氏名_作品名 (例: s24001_山田太郎_game.html / 24001-山田-calc.html)
  const parts = baseName.split(/[_ー－\-]+/).map(s => s.trim()).filter(Boolean);
  if (parts.length >= 3 && /^[a-zA-Z0-9]{4,12}$/.test(parts[0])) {
    author = `${parts[0]} ${parts[1]}`.slice(0, 20);
    nameDerivedTitle = parts.slice(2).join(' ');
  } else if (parts.length === 2 && /^[a-zA-Z0-9]{4,12}$/.test(parts[0])) {
    author = parts[0].slice(0, 20);
    nameDerivedTitle = parts[1];
  } else if (parts.length >= 2) {
    author = parts[0].slice(0, 20);
    nameDerivedTitle = parts.slice(1).join(' ');
  }

  // HTMLの <title> タグ抽出（汎用名でない場合のみ採用）
  let title = nameDerivedTitle || baseName;
  const titleMatch = htmlContent.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  if (titleMatch && titleMatch[1]) {
    const rawHtmlTitle = titleMatch[1].replace(/\s+/g, ' ').trim();
    const genericTitles = ['document', 'untitled', 'index', 'html', 'page', '無題', 'テスト', 'test'];
    if (rawHtmlTitle && !genericTitles.includes(rawHtmlTitle.toLowerCase())) {
      title = rawHtmlTitle;
    }
  }

  return {
    title: (title || baseName).slice(0, 40),
    author: author || '匿名',
    genre: defaultGenre,
  };
}

/**
 * 複数HTMLファイル一括インポートモーダル（限定公開デフォルト）
 */
function openBulkImportModal(
  classes: ClassItem[],
  defaultClassId: string | undefined,
  onImported: () => void
) {
  if (classes.length === 0) {
    showToast('先に授業を作成してください', 'error');
    return;
  }

  const modalRoot = document.getElementById('modal-root')!;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay active';

  let queuedFiles: QueuedImportFile[] = [];

  overlay.innerHTML = `
    <div class="modal-dialog modal-lg" role="dialog" aria-modal="true" aria-labelledby="bulk-import-title" style="max-width: 860px;">
      <div class="modal-header">
        <div class="modal-title" id="bulk-import-title">
          <span>📥</span> 学生HTMLファイルの一括インポート（限定公開）
        </div>
        <button id="btn-close-bulk" class="btn btn-text btn-sm" type="button">✕</button>
      </div>
      <div class="modal-body" style="padding: 20px; display: flex; flex-direction: column; gap: 16px; max-height: 75vh; overflow-y: auto;">
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; background: #f8fafc; padding: 14px; border-radius: 8px; border: 1px solid var(--color-border-subtle);">
          <div class="form-group" style="margin: 0;">
            <label class="form-label" for="bulk-class-select">投入先の授業 <span class="badge-req">必須</span></label>
            <select id="bulk-class-select" class="form-select">
              ${classes.map(c => `<option value="${escapeHtml(c.id)}" ${c.id === defaultClassId ? 'selected' : ''}>${escapeHtml(c.name)} (${escapeHtml(c.id)})</option>`).join('')}
            </select>
          </div>

          <div class="form-group" style="margin: 0;">
            <label class="form-label" for="bulk-visibility-select">公開範囲</label>
            <select id="bulk-visibility-select" class="form-select">
              <option value="private" selected>🔒 限定公開（授業内のみ・推奨）</option>
              <option value="public">🌐 完全公開（全体公開ライブラリにも掲載）</option>
            </select>
          </div>

          <div class="form-group" style="margin: 0;">
            <label class="form-label" for="bulk-genre-select">デフォルトジャンル</label>
            <select id="bulk-genre-select" class="form-select">
              <option value="その他">その他</option>
              <option value="ゲーム">ゲーム</option>
              <option value="ツール">ツール</option>
              <option value="アニメーション">アニメーション</option>
              <option value="ポートフォリオ">ポートフォリオ</option>
              <option value="アート">アート</option>
            </select>
          </div>

          <div class="form-group" style="margin: 0;">
            <label class="form-label" for="bulk-thumb-mode">サムネイル生成方式</label>
            <select id="bulk-thumb-mode" class="form-select">
              <option value="fast" selected>⚡ 高速一括登録（ジャンル別デザイン自動生成）</option>
              <option value="capture">📸 画面キャプチャ生成（1件ずつ描画撮影）</option>
            </select>
          </div>
        </div>

        <!-- ドラッグ＆ドロップ領域 -->
        <div
          id="bulk-dropzone"
          style="border: 2px dashed var(--color-primary); border-radius: 10px; padding: 28px 20px; text-align: center; background: #f8fbff; cursor: pointer; transition: background 0.15s;"
        >
          <div style="font-size: 32px; margin-bottom: 6px;">📂</div>
          <div style="font-weight: 600; font-size: 15px; color: var(--color-text-primary);">
            ここに回収したHTMLファイル群をまとめてドラッグ＆ドロップ
          </div>
          <div style="font-size: 12px; color: var(--color-text-secondary); margin-top: 4px;">
            またはクリックして複数ファイルを選択（<code>.html</code> / <code>.htm</code> 複数選択可）<br>
            ※ <code>学籍番号_氏名_作品名.html</code> や HTML内の <code>&lt;title&gt;</code> から作品名・作者名を自動抽出します（下の表で個別編集も可能）
          </div>
          <input type="file" id="bulk-file-input" accept=".html,.htm" multiple style="display: none;" />
        </div>

        <!-- キュー一覧テーブル -->
        <div id="bulk-queue-container" style="display: none; flex-direction: column; gap: 8px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <strong id="bulk-queue-count" style="font-size: 13px;">選択中: 0件</strong>
            <button type="button" id="btn-clear-bulk-queue" class="btn btn-text btn-sm" style="color: var(--color-danger);">一覧をクリア</button>
          </div>
          <div style="border: 1px solid var(--color-border); border-radius: 8px; max-height: 260px; overflow-y: auto;">
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <thead style="background: #fafbfc; position: sticky; top: 0; border-bottom: 1px solid var(--color-border);">
                <tr>
                  <th style="padding: 8px 10px; text-align: left;">ファイル名</th>
                  <th style="padding: 8px 10px; text-align: left;">作品名 (編集可)</th>
                  <th style="padding: 8px 10px; text-align: left;">制作者名 (編集可)</th>
                  <th style="padding: 8px 10px; text-align: left;">ジャンル</th>
                  <th style="padding: 8px 10px; text-align: right;">サイズ</th>
                  <th style="padding: 8px 10px; text-align: center;">除外</th>
                </tr>
              </thead>
              <tbody id="bulk-queue-tbody"></tbody>
            </table>
          </div>
        </div>

        <!-- 進捗表示 -->
        <div id="bulk-progress-box" style="display: none; background: #f1f5f9; padding: 12px 16px; border-radius: 8px;">
          <div style="display: flex; justify-content: space-between; font-size: 12px; font-weight: 600; margin-bottom: 6px;">
            <span id="bulk-progress-label">アップロード中...</span>
            <span id="bulk-progress-num">0 / 0</span>
          </div>
          <div style="width: 100%; height: 8px; background: #cbd5e1; border-radius: 4px; overflow: hidden;">
            <div id="bulk-progress-bar" style="width: 0%; height: 100%; background: var(--color-primary); transition: width 0.2s;"></div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button id="btn-cancel-bulk" class="btn btn-text" type="button">キャンセル</button>
        <button id="btn-run-bulk-import" class="btn btn-primary" type="button" disabled>
          🚀 一括インポートを実行 (0件)
        </button>
      </div>
    </div>
  `;

  modalRoot.appendChild(overlay);

  const cleanup = () => {
    overlay.classList.remove('active');
    setTimeout(() => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 200);
  };

  overlay.querySelector('#btn-close-bulk')!.addEventListener('click', cleanup);
  overlay.querySelector('#btn-cancel-bulk')!.addEventListener('click', cleanup);

  const dropzone = overlay.querySelector('#bulk-dropzone') as HTMLElement;
  const fileInput = overlay.querySelector('#bulk-file-input') as HTMLInputElement;
  const defaultGenreSelect = overlay.querySelector('#bulk-genre-select') as HTMLSelectElement;
  const queueContainer = overlay.querySelector('#bulk-queue-container') as HTMLElement;
  const queueCount = overlay.querySelector('#bulk-queue-count') as HTMLElement;
  const queueTbody = overlay.querySelector('#bulk-queue-tbody') as HTMLElement;
  const runBtn = overlay.querySelector('#btn-run-bulk-import') as HTMLButtonElement;

  const renderQueue = () => {
    if (queuedFiles.length === 0) {
      queueContainer.style.display = 'none';
      runBtn.disabled = true;
      runBtn.textContent = '🚀 一括インポートを実行 (0件)';
      return;
    }

    queueContainer.style.display = 'flex';
    queueCount.textContent = `投入予定ファイル: ${queuedFiles.length} 件`;
    runBtn.disabled = false;
    runBtn.textContent = `🚀 ${queuedFiles.length}件を一括インポート`;

    queueTbody.innerHTML = queuedFiles.map((item, idx) => `
      <tr style="border-bottom: 1px solid var(--color-border-subtle);">
        <td style="padding: 6px 10px; font-family: var(--font-family-mono); max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${escapeHtml(item.fileName)}">
          ${escapeHtml(item.fileName)}
        </td>
        <td style="padding: 6px 10px;">
          <input type="text" class="form-input bulk-edit-title" data-idx="${idx}" value="${escapeHtml(item.title)}" style="height: 28px; font-size: 12px; padding: 2px 8px;" />
        </td>
        <td style="padding: 6px 10px;">
          <input type="text" class="form-input bulk-edit-author" data-idx="${idx}" value="${escapeHtml(item.author)}" style="height: 28px; font-size: 12px; padding: 2px 8px; width: 120px;" />
        </td>
        <td style="padding: 6px 10px;">
          <select class="form-select bulk-edit-genre" data-idx="${idx}" style="height: 28px; font-size: 12px; padding: 2px 6px;">
            ${(['その他', 'ゲーム', 'ツール', 'アニメーション', 'ポートフォリオ', 'アート'] as GenreType[]).map(g => `
              <option value="${g}" ${item.genre === g ? 'selected' : ''}>${g}</option>
            `).join('')}
          </select>
        </td>
        <td style="padding: 6px 10px; text-align: right; white-space: nowrap; color: var(--color-text-secondary);">
          ${(item.fileSizeBytes / 1024).toFixed(1)} KB
        </td>
        <td style="padding: 6px 10px; text-align: center;">
          <button type="button" class="btn btn-text btn-sm bulk-remove-row" data-idx="${idx}" style="color: var(--color-danger); padding: 2px 6px;">✕</button>
        </td>
      </tr>
    `).join('');

    queueTbody.querySelectorAll('.bulk-edit-title').forEach((inp) => {
      inp.addEventListener('input', (e) => {
        const idx = parseInt((e.target as HTMLElement).getAttribute('data-idx') || '0', 10);
        queuedFiles[idx].title = (e.target as HTMLInputElement).value;
      });
    });

    queueTbody.querySelectorAll('.bulk-edit-author').forEach((inp) => {
      inp.addEventListener('input', (e) => {
        const idx = parseInt((e.target as HTMLElement).getAttribute('data-idx') || '0', 10);
        queuedFiles[idx].author = (e.target as HTMLInputElement).value;
      });
    });

    queueTbody.querySelectorAll('.bulk-edit-genre').forEach((sel) => {
      sel.addEventListener('change', (e) => {
        const idx = parseInt((e.target as HTMLElement).getAttribute('data-idx') || '0', 10);
        queuedFiles[idx].genre = (e.target as HTMLSelectElement).value as GenreType;
      });
    });

    queueTbody.querySelectorAll('.bulk-remove-row').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.getAttribute('data-idx') || '0', 10);
        queuedFiles.splice(idx, 1);
        renderQueue();
      });
    });
  };

  const handleSelectedFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const defaultGenre = (defaultGenreSelect.value || 'その他') as GenreType;

    for (const file of Array.from(fileList)) {
      const lower = file.name.toLowerCase();
      if (!lower.endsWith('.html') && !lower.endsWith('.htm')) {
        showToast(`${file.name} はHTMLファイルではないためスキップしました`, 'error');
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast(`${file.name} は5MBを超えているためスキップしました`, 'error');
        continue;
      }
      const text = await file.text();
      const meta = inferMetadataFromFile(file.name, text, defaultGenre);

      // 既に同名ファイルがキューにあれば上書き
      const existingIdx = queuedFiles.findIndex(q => q.fileName === file.name);
      const entry: QueuedImportFile = {
        fileName: file.name,
        fileContent: text,
        fileSizeBytes: file.size,
        title: meta.title,
        author: meta.author,
        genre: meta.genre,
      };
      if (existingIdx >= 0) {
        queuedFiles[existingIdx] = entry;
      } else {
        queuedFiles.push(entry);
      }
    }
    renderQueue();
  };

  dropzone.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    handleSelectedFiles(fileInput.files);
    fileInput.value = '';
  });

  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.style.background = '#e0f2fe';
  });
  dropzone.addEventListener('dragleave', () => {
    dropzone.style.background = '#f8fbff';
  });
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.style.background = '#f8fbff';
    handleSelectedFiles(e.dataTransfer?.files || null);
  });

  defaultGenreSelect.addEventListener('change', () => {
    const newGenre = defaultGenreSelect.value as GenreType;
    queuedFiles.forEach(q => { q.genre = newGenre; });
    renderQueue();
  });

  overlay.querySelector('#btn-clear-bulk-queue')!.addEventListener('click', () => {
    queuedFiles = [];
    renderQueue();
  });

  runBtn.addEventListener('click', async () => {
    if (queuedFiles.length === 0) return;

    const targetClassId = (overlay.querySelector('#bulk-class-select') as HTMLSelectElement).value;
    const visibility = (overlay.querySelector('#bulk-visibility-select') as HTMLSelectElement).value as VisibilityType;
    const thumbMode = (overlay.querySelector('#bulk-thumb-mode') as HTMLSelectElement).value;

    const progressBox = overlay.querySelector('#bulk-progress-box') as HTMLElement;
    const progressLabel = overlay.querySelector('#bulk-progress-label') as HTMLElement;
    const progressNum = overlay.querySelector('#bulk-progress-num') as HTMLElement;
    const progressBar = overlay.querySelector('#bulk-progress-bar') as HTMLElement;

    progressBox.style.display = 'block';
    runBtn.disabled = true;

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < queuedFiles.length; i++) {
      const item = queuedFiles[i];
      progressLabel.textContent = `登録中: ${item.fileName}`;
      progressNum.textContent = `${i + 1} / ${queuedFiles.length}`;
      progressBar.style.width = `${Math.round(((i + 1) / queuedFiles.length) * 100)}%`;

      try {
        let thumbnailUrl = generateGenreFallback(item.genre, item.title);
        if (thumbMode === 'capture') {
          const captured = await captureThumbnail(item.fileContent, item.genre, item.title, 6000);
          thumbnailUrl = captured.dataUrl;
        }

        await api.uploadSubmission({
          classId: targetClassId,
          fileName: item.fileName,
          fileContent: item.fileContent,
          title: item.title.trim() || item.fileName,
          genre: item.genre,
          author: item.author.trim() || '匿名',
          description: '教員による一括インポート登録',
          visibility,
          thumbnailUrl,
          fileSizeBytes: item.fileSizeBytes,
        });
        successCount++;
      } catch (err) {
        console.error(`Failed to import ${item.fileName}:`, err);
        failCount++;
      }
    }

    if (failCount === 0) {
      showToast(`${successCount}件のHTMLファイルを「${visibility === 'private' ? '限定公開' : '完全公開'}」として一括登録しました！`, 'success');
    } else {
      showToast(`完了: 成功 ${successCount}件 / 失敗 ${failCount}件`, 'info');
    }

    cleanup();
    onImported();
  });
}

/**
 * HTML直接編集＆メタデータ編集モーダル
 */
async function openHtmlEditorModal(sub: SubmissionItem, onSaved: () => void) {
  const modalRoot = document.getElementById('modal-root')!;
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay active';

  const initialHtml = sub.htmlContent || await api.getRawHtml(sub.classId, sub.fileName);

  overlay.innerHTML = `
    <div class="modal-dialog modal-lg" role="dialog" aria-modal="true" aria-labelledby="editor-title">
      <div class="modal-header">
        <div class="modal-title" id="editor-title">
          <span>✏️</span> 提出物の編集: ${escapeHtml(sub.fileName)}
        </div>
        <button id="btn-close-editor" class="btn btn-text btn-sm" type="button">✕</button>
      </div>
      <div class="modal-body" style="padding: 16px; display: flex; flex-direction: column; gap: 12px;">
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid var(--color-border-subtle);">
          <div>
            <label class="form-label" style="font-size: 11px;">作品名</label>
            <input type="text" id="edit-meta-title" class="form-input" value="${escapeHtml(sub.title)}" style="height: 32px; font-size: 12px;" />
          </div>
          <div>
            <label class="form-label" style="font-size: 11px;">制作者名</label>
            <input type="text" id="edit-meta-author" class="form-input" value="${escapeHtml(sub.author)}" style="height: 32px; font-size: 12px;" />
          </div>
          <div>
            <label class="form-label" style="font-size: 11px;">ジャンル</label>
            <select id="edit-meta-genre" class="form-select" style="height: 32px; font-size: 12px;">
              ${(['ゲーム', 'ツール', 'アニメーション', 'ポートフォリオ', 'アート', 'その他'] as GenreType[]).map(g => `
                <option value="${g}" ${sub.genre === g ? 'selected' : ''}>${g}</option>
              `).join('')}
            </select>
          </div>
          <div>
            <label class="form-label" style="font-size: 11px;">公開範囲</label>
            <select id="edit-meta-visibility" class="form-select" style="height: 32px; font-size: 12px;">
              <option value="private" ${sub.visibility === 'private' ? 'selected' : ''}>🔒 限定公開 (授業内のみ)</option>
              <option value="public" ${sub.visibility === 'public' ? 'selected' : ''}>🌐 完全公開 (全体)</option>
            </select>
          </div>
        </div>
        <textarea
          id="editor-code"
          class="form-textarea"
          style="font-family: var(--font-family-mono); font-size: 12px; line-height: 1.4; flex: 1; min-height: 360px; tab-size: 2; white-space: pre;"
        >${escapeHtml(initialHtml)}</textarea>
      </div>
      <div class="modal-footer">
        <button id="btn-cancel-editor" class="btn btn-text" type="button">キャンセル</button>
        <button id="btn-save-editor" class="btn btn-primary" type="button">💾 保存して更新</button>
      </div>
    </div>
  `;

  modalRoot.appendChild(overlay);

  const cleanup = () => {
    overlay.classList.remove('active');
    setTimeout(() => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 200);
  };

  overlay.querySelector('#btn-close-editor')!.addEventListener('click', cleanup);
  overlay.querySelector('#btn-cancel-editor')!.addEventListener('click', cleanup);

  overlay.querySelector('#btn-save-editor')!.addEventListener('click', async () => {
    const code = (overlay.querySelector('#editor-code') as HTMLTextAreaElement).value;
    const title = (overlay.querySelector('#edit-meta-title') as HTMLInputElement).value.trim();
    const author = (overlay.querySelector('#edit-meta-author') as HTMLInputElement).value.trim();
    const genre = (overlay.querySelector('#edit-meta-genre') as HTMLSelectElement).value as GenreType;
    const visibility = (overlay.querySelector('#edit-meta-visibility') as HTMLSelectElement).value as VisibilityType;

    await api.updateSubmission(sub.classId, sub.fileName, {
      title: title || sub.title,
      author: author || '匿名',
      genre,
      visibility,
      htmlContent: code,
      fileSizeBytes: new Blob([code]).size
    });
    showToast('提出物のメタデータとHTMLを更新しました！', 'success');
    cleanup();
    onSaved();
  });
}

/**
 * リストア＆バックアップ管理タブ
 */
function renderBackupsTab(container: HTMLElement, backups: BackupItem[], onRefresh: () => void) {
  container.innerHTML = `
    <div style="background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; overflow-x: auto;">
      <table style="width: 100%; border-collapse: collapse; font-size: 13px; text-align: left;">
        <thead style="background: #fafbfc; border-bottom: 1px solid var(--color-border);">
          <tr>
            <th style="padding: 12px 16px;">元ファイル名</th>
            <th style="padding: 12px 16px;">授業ID</th>
            <th style="padding: 12px 16px;">退避日時</th>
            <th style="padding: 12px 16px;">タイトル / 作者</th>
            <th style="padding: 12px 16px; text-align: right;">アクション</th>
          </tr>
        </thead>
        <tbody>
          ${backups.length === 0 ? `
            <tr><td colspan="5" style="padding: 32px; text-align: center; color: var(--color-text-secondary);">バックアップ履歴はありません</td></tr>
          ` : backups.map((bak) => `
            <tr style="border-bottom: 1px solid var(--color-border-subtle);">
              <td style="padding: 12px 16px;">
                <code style="font-weight: 600;">${bak.originalFileName}</code>
              </td>
              <td style="padding: 12px 16px;">${bak.classId}</td>
              <td style="padding: 12px 16px;">${new Date(bak.overwrittenAt).toLocaleString('ja-JP')}</td>
              <td style="padding: 12px 16px;">
                <div>${escapeHtml(bak.title)}</div>
                <div style="font-size: 11px; color: var(--color-text-secondary);">${escapeHtml(bak.author)}</div>
              </td>
              <td style="padding: 12px 16px; text-align: right; white-space: nowrap;">
                <button class="btn btn-outlined btn-sm btn-restore" data-id="${bak.backupId}" type="button">
                  ↩️ 復元 (Restore)
                </button>
                <button class="btn btn-text btn-sm btn-publish-as" data-id="${bak.backupId}" data-file="${bak.originalFileName}" type="button">
                  別名で公開
                </button>
                <button class="btn btn-text btn-sm btn-del-backup" data-id="${bak.backupId}" style="color: var(--color-danger);" type="button">
                  消去
                </button>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;

  // 復旧（Restore）
  container.querySelectorAll('.btn-restore').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-id')!;
      if (confirm('このバックアップを最新ファイルとして復旧しますか？\n（現在の最新ファイルは新たなバックアップに自動退避されます）')) {
        await api.restoreBackup(id);
        showToast('バックアップファイルを復元しました！', 'success');
        onRefresh();
      }
    });
  });

  // 別名で公開（Publish As）
  container.querySelectorAll('.btn-publish-as').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-id')!;
      const oldFile = btn.getAttribute('data-file')!;
      const defaultNew = oldFile.replace('.html', '_restored.html');
      const newName = prompt('新しく公開するファイル名を入力してください:', defaultNew);
      if (newName) {
        await api.publishBackupAs(id, newName.trim());
        showToast(`「${newName}」として別名公開しました！`, 'success');
        onRefresh();
      }
    });
  });

  // 完全削除
  container.querySelectorAll('.btn-del-backup').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-id')!;
      if (confirm('このバックアップ履歴を完全に削除しますか？')) {
        await api.deleteBackup(id);
        showToast('バックアップを削除しました', 'success');
        onRefresh();
      }
    });
  });
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
